export type NormalizedRenderPass = { type: string; [key: string]: any };

type NormalizeOptions = {
    forceFxaa?: boolean;
};

const LEGACY_PASS_KEYS: Record<string, string[]> = {
    bloom: ["threshold", "strength", "radius", "bloomThreshold", "bloomStrength", "bloomRadius"],
    dotScreen: ["scale", "angle"],
    noise: ["amount", "threshold"],
    lut: ["cubeMapUrl", "intensity"],
    fxaa: ["resolution", "fxaaResolution"],
};

const clonePass = (pass: any): NormalizedRenderPass | null => {
    if (!pass || typeof pass !== "object" || !pass.type) return null;
    return { ...pass };
};

const normalizeLegacyPass = (type: string, postProcessing: any): NormalizedRenderPass => {
    const pass: NormalizedRenderPass = { type };
    const keys = LEGACY_PASS_KEYS[type] || [];

    keys.forEach((key) => {
        if (postProcessing[key] !== undefined) {
            pass[key.replace(/^bloom/, "").replace(/^fxaa/, "").replace(/^[A-Z]/, (letter) => letter.toLowerCase())] = postProcessing[key];
        }
    });

    if (type === "bloom") {
        if (postProcessing.bloomThreshold !== undefined) pass.threshold = postProcessing.bloomThreshold;
        if (postProcessing.bloomStrength !== undefined) pass.strength = postProcessing.bloomStrength;
        if (postProcessing.bloomRadius !== undefined) pass.radius = postProcessing.bloomRadius;
    }

    if (type === "fxaa" && postProcessing.fxaaResolution !== undefined) {
        pass.resolution = postProcessing.fxaaResolution;
    }

    return pass;
};

const passesFromLegacyObject = (postProcessing: any): NormalizedRenderPass[] => {
    if (!postProcessing || typeof postProcessing !== "object") return [];
    const passOrder = Array.isArray(postProcessing.passOrder)
        ? postProcessing.passOrder.filter(Boolean)
        : [];

    return passOrder
        .map((type: string) => normalizeLegacyPass(String(type), postProcessing))
        .filter((pass: NormalizedRenderPass) => Boolean(pass?.type));
};

export const normalizePostProcessingPasses = (
    postProcessing: any,
    options: NormalizeOptions = {}
): NormalizedRenderPass[] => {
    const passes = Array.isArray(postProcessing)
        ? postProcessing.map(clonePass).filter(Boolean) as NormalizedRenderPass[]
        : passesFromLegacyObject(postProcessing);

    if (!passes.some((pass) => pass.type === "render")) {
        passes.unshift({ type: "render" });
    }

    if (options.forceFxaa && !passes.some((pass) => pass.type === "fxaa")) {
        const renderIndex = passes.findIndex((pass) => pass.type === "render");
        passes.splice(renderIndex >= 0 ? renderIndex + 1 : 1, 0, { type: "fxaa" });
    }

    return passes;
};

export const isMoMPostProcessingTarget = (galleryData?: any, spaceData?: any): boolean => {
    const spaceType = String(galleryData?.spaceType || spaceData?.type || "").toLowerCase();
    return (spaceType === "mom" || spaceType === "sellermom") && spaceData?.subtype !== "pseudoMoM";
};
